import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  auth, 
  db, 
  googleProvider, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateFirebaseProfile,
  sendPasswordResetEmail,
  doc, 
  getDoc, 
  setDoc,
  Timestamp,
  updateDoc
} from '@/firebase';
import { User as FirebaseUser, browserPopupRedirectResolver, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { toast } from 'sonner';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  phoneNumber?: string;
  companyName?: string;
  address?: string;
  role: 'admin' | 'user';
  createdAt: any;
}

interface AuthContextType {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  loading: boolean;
  isLoggingIn: boolean;
  isAdmin: boolean;
  isAuthModalOpen: boolean;
  authModalMode: 'login' | 'register';
  authModalNotice?: string;
  openAuthModal: (mode?: 'login' | 'register', notice?: string) => void;
  closeAuthModal: () => void;
  login: () => Promise<void>;
  loginWithEmail: (email: string, pass: string) => Promise<void>;
  registerWithEmail: (email: string, pass: string, name: string, phoneNumber?: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');
  const [authModalNotice, setAuthModalNotice] = useState<string | undefined>(undefined);

  const openAuthModal = (mode: 'login' | 'register' = 'login', notice?: string) => {
    setAuthModalMode(mode);
    setAuthModalNotice(notice);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => {
    setIsAuthModalOpen(false);
    setAuthModalNotice(undefined);
  };

  useEffect(() => {
    if (!auth || !auth.app) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        // Fetch or create profile
        const profileRef = doc(db, 'users', user.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          const data = profileSnap.data() as UserProfile;
          // Ensure roles are synced with the latest requirements
          try {
            if ((user.email === 'azmankoe916@gmail.com' || user.email === 'azmancivd@gmail.com') && data.role !== 'admin') {
              await updateDoc(profileRef, { role: 'admin' });
              data.role = 'admin';
            }
          } catch (e) {
            console.error("Failed to sync role:", e);
          }
          setProfile({ ...data }); // Use spread to ensure new object reference
        } else {
          // Create new profile
          const newProfile: UserProfile = {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            role: (user.email === 'azmankoe916@gmail.com' || user.email === 'azmancivd@gmail.com') ? 'admin' : 'user',
            createdAt: Timestamp.now(),
          };
          await setDoc(profileRef, newProfile);
          setProfile(newProfile);
        }
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async () => {
    if (isLoggingIn) return;
    if (!auth || !googleProvider || !auth.app) {
      toast.error("Firebase belum terkonfigurasi. Silakan hubungi admin.");
      return;
    }
    setIsLoggingIn(true);
    try {
      // Set persistence to local to ensure session survives refreshes
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, googleProvider, browserPopupRedirectResolver);
    } catch (error: any) {
      if (error.code === 'auth/network-request-failed') {
        toast.error("Gagal terhubung ke server. Periksa koneksi internet Anda.");
      } else if (error.code === 'auth/popup-closed-by-user') {
        toast.error("Login dibatalkan.");
      } else if (error.code === 'auth/popup-blocked') {
        toast.error("Popup diblokir! Harap izinkan popup di browser Anda untuk login.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        // Ignore this one as it's usually a duplicate call
      } else {
        console.error('Login error:', error);
        toast.error("Gagal login dengan Google.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const loginWithEmail = async (email: string, pass: string) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      await setPersistence(auth, browserLocalPersistence);
      await signInWithEmailAndPassword(auth, email, pass);
      toast.success("Berhasil masuk!");
    } catch (error: any) {
      if (error.code === 'auth/operation-not-allowed') {
        toast.error("Metode Email/Password belum diaktifkan di Firebase Console.");
      } else if (error.code === 'auth/invalid-email') {
        toast.error("Format email tidak valid.");
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        toast.error("Email atau password salah.");
      } else {
        console.error('Email login error:', error);
        toast.error("Gagal masuk: " + error.message);
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const registerWithEmail = async (email: string, pass: string, name: string, phoneNumber?: string) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, pass);
      await updateFirebaseProfile(user, { displayName: name });

      let formattedPhone = '';
      if (phoneNumber) {
        const cleanDigits = phoneNumber.replace(/[^0-9]/g, '');
        if (cleanDigits.startsWith('62')) {
          formattedPhone = `+${cleanDigits}`;
        } else if (cleanDigits.startsWith('0')) {
          formattedPhone = `+62${cleanDigits.substring(1)}`;
        } else {
          formattedPhone = `+62${cleanDigits}`;
        }
      }

      const profileRef = doc(db, 'users', user.uid);
      const newProfile: UserProfile = {
        uid: user.uid,
        email: user.email || '',
        displayName: name,
        phoneNumber: formattedPhone,
        photoURL: '',
        role: (user.email === 'azmankoe916@gmail.com' || user.email === 'azmancivd@gmail.com') ? 'admin' : 'user',
        createdAt: Timestamp.now(),
      };
      await setDoc(profileRef, newProfile);
      setProfile(newProfile);

      toast.success("Pendaftaran berhasil!");
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        toast.error("Email sudah terdaftar. Silakan gunakan menu Masuk.");
      } else {
        console.error('Registration error:', error);
        toast.error("Gagal mendaftar. Silakan coba lagi.");
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const resetPassword = async (email: string) => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    try {
      if (!auth || !auth.app) {
        toast.error("Firebase belum terkonfigurasi. Silakan hubungi admin.");
        return;
      }
      await sendPasswordResetEmail(auth, email);
      toast.success("Tautan pengaturan ulang kata sandi telah dikirim ke email Anda.");
    } catch (error: any) {
      console.error('Reset password error:', error);
      if (error.code === 'auth/user-not-found') {
        toast.error("Tidak ada pengguna yang ditemukan dengan email ini.");
      } else if (error.code === 'auth/invalid-email') {
        toast.error("Format email tidak valid.");
      } else {
        toast.error("Gagal mengirim tautan reset password. Silakan coba lagi.");
      }
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = async () => {
    if (!auth || !auth.app) return;
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user || !db || !auth.app) return;
    try {
      const profileRef = doc(db, 'users', user.uid);
      await updateDoc(profileRef, { ...data, updatedAt: Timestamp.now() });
      setProfile(prev => prev ? { ...prev, ...data } : null);
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  };

  const isAdmin = profile?.role === 'admin' || (!!user && (user.email === 'azmankoe916@gmail.com' || user.email === 'azmancivd@gmail.com'));

  return (
    <AuthContext.Provider value={{ 
      user, 
      profile, 
      loading, 
      isLoggingIn, 
      isAdmin, 
      isAuthModalOpen,
      authModalMode,
      authModalNotice,
      openAuthModal,
      closeAuthModal,
      login, 
      loginWithEmail, 
      registerWithEmail, 
      logout, 
      updateProfile, 
      resetPassword 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
